import type { ICompany, UUID, IShift } from '@shiftsmartinc/shiftsmart-types';

import { dispatch } from 'rfx-core';
import moment from 'moment-timezone';
import _ from 'lodash';

import { getChildLogger } from './client.logger';

const fileLog = getChildLogger('utils.shifts');

interface IGetGeneratedSmartTitleArgs {
  company?: ICompany['uuid'];
  companyObj?: Partial<ICompany>;
  end?: Date | string;
  role: UUID;
  roleObj?: Partial<{ title: string }>;
  roleTitle?: string;
  shiftObj?: Partial<IShift>;
  start?: Date | string;
}

const DEFAULT_TEMPLATE_STRING =
  '<%= formatted.company%> - <%= roleTitle ? (roleTitle + " - ") : "" %> <%= formatted.dateRange%>';

/**
 * Use to generate the shift title
 * Assuming the shiftTitleTemplate be like "<%= formatted.company%> - <%= roleTitle%> - <%= formattedDateRange%>"
 * @return {string} shiftTitleTemplate
 */

export async function getGeneratedSmartTitle({
  role,
  roleObj = {},
  start,
  end,
  company,
  companyObj = {},
  roleTitle,
  shiftObj = {},
}: IGetGeneratedSmartTitleArgs) {
  if (_.isEmpty(companyObj) && !_.isEmpty(company)) {
    _.assign(
      companyObj,
      await dispatch('companies.get', company, {
        select: false,
      }),
    );
  }

  const rawTemplateString = DEFAULT_TEMPLATE_STRING;
  if (/^[\s'"]*false/i.test(rawTemplateString)) {
    return undefined;
  }
  const shiftTitleTemplate = _.template(rawTemplateString);

  if (_.isEmpty(roleObj) && !_.isEmpty(role)) {
    _.assign(
      roleObj,
      await dispatch('positions.get', role, {
        select: false,
      }),
    );
  }

  const formatted = {
    company: companyObj.abbr || companyObj.name,
    endDay: moment(end).format('ddd'),
    endTime: moment(end).format('h:mmA'),
    startDay: moment(start).format('ddd'),
    startTime: moment(start).format('h:mmA'),
  };
  _.extend(formatted, {
    dateRange: `${formatted.startDay} (${formatted.startTime}-${formatted.endTime})`,
  });

  return shiftTitleTemplate({
    companyObj,
    formatted,
    roleObj,
    roleTitle: roleTitle || roleObj?.title,
    shiftObj,
  });
}

export function validateFlexibleShiftWorkingHours({
  shiftStart,
  shiftEnd,
  shiftScheduleRules,
  ignoreDateDifference = false,
  timezone = 'UTC',
}: {
  ignoreDateDifference?: boolean;
  shiftEnd: Date | string;
  shiftScheduleRules: {
    workingHoursEnd?: Date | string;
    workingHoursStart?: Date | string;
  };
  shiftStart: Date | string;
  timezone?: string;
}) {
  const { workingHoursStart, workingHoursEnd } = shiftScheduleRules ?? {};

  // moment(undefined) returns now, so an absent bound would be invented here and
  // then written back into the form as a real edit. Keep absence as null.
  const newWorkingHoursStartValue = workingHoursStart
    ? moment(workingHoursStart)
    : null;
  const newWorkingHoursEndValue = workingHoursEnd
    ? moment(workingHoursEnd)
    : null;

  // Form stores dates in user's local TZ but displays in shift's TZ.
  // Extract local representation and interpret in shift's timezone for comparison.
  const currentTimePassedWorkingHoursStart = newWorkingHoursStartValue
    ? moment
        .tz(timezone)
        .isAfter(
          moment.tz(
            newWorkingHoursStartValue.format('YYYY-MM-DDTHH:mm:ss'),
            timezone,
          ),
        )
    : false;

  let flexibleShiftValidationError = '';
  let errorPath = '';
  if (newWorkingHoursStartValue?.isAfter(shiftStart)) {
    flexibleShiftValidationError =
      'Working Hours cannot start after shift start';
    errorPath = 'shiftScheduleRules.workingHoursStart';
  } else if (
    newWorkingHoursEndValue?.isBefore(shiftEnd) &&
    !currentTimePassedWorkingHoursStart
  ) {
    flexibleShiftValidationError = 'Working Hours cannot end before shift end';
    errorPath = 'shiftScheduleRules.workingHoursEnd';
  } else if (
    newWorkingHoursStartValue &&
    newWorkingHoursEndValue &&
    newWorkingHoursStartValue.isAfter(newWorkingHoursEndValue)
  ) {
    flexibleShiftValidationError =
      'Working Hours Start must be before Working Hours End';
    errorPath = 'shiftScheduleRules.workingHoursStart';
  }

  return {
    errorPath,
    flexibleShiftValidationError,
    newWorkingHoursEndValue: newWorkingHoursEndValue?.toDate(),
    newWorkingHoursStartValue: newWorkingHoursStartValue?.toDate(),
  };
}

export function validateShiftDuration({ start, end }): string | null {
  const log = fileLog.getChildLogger('validateShiftDuration');
  const duration = moment(end).diff(start, 'hours');
  log.debug('Shift has a duration of %s hours', duration);
  if (duration > 48) {
    return 'Shifts cannot be longer than 48 hours';
  }

  return null;
}
